import { FormEvent, useState } from "react";
import { Loader2 } from "lucide-react";
import { isInvalidChecksum } from "../utils/activationResolver";
import { calculateAudibleChecksum, isValidActivationBytes } from "../utils/audibleHash";

const CHECKSUM_INPUT_ID = "expert-mode-checksum";
const ACTIVATION_BYTES_INPUT_ID = "expert-mode-activation-bytes";
const RESOLVER_ERROR_ID = "expert-mode-resolver-error";

type ExpertModeResolverProps = {
  checksum: string;
  setChecksum: (checksum: string) => void;
  activationBytes: string;
  setActivationBytes: (activationBytes: string) => void;
  isResolvingActivationBytes: boolean;
  onResolveActivationBytes: (checksum: string) => Promise<boolean>;
  isDark: boolean;
};

export function ExpertModeResolver({
  checksum,
  setChecksum,
  activationBytes,
  setActivationBytes,
  isResolvingActivationBytes,
  onResolveActivationBytes,
  isDark,
}: ExpertModeResolverProps) {
  const [isCalculatingHash, setIsCalculatingHash] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const inputClasses = `min-h-11 min-w-0 flex-1 rounded p-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm ${
    isDark ? "bg-gray-700 text-gray-100" : "bg-white text-gray-900 border border-gray-300"
  }`;
  const buttonClasses = `min-h-11 w-full shrink-0 cursor-pointer rounded bg-blue-600 px-4 py-2 text-base font-medium text-white transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-48 sm:text-sm ${
    isDark ? "focus:ring-offset-gray-800" : "focus:ring-offset-gray-100"
  }`;

  const handleActivationBytesLookup = async (event: FormEvent) => {
    event.preventDefault();
    setErrorMessage("");

    if (isInvalidChecksum(checksum)) {
      setErrorMessage("Checksum must contain exactly 40 hexadecimal characters.");
      return;
    }

    const wasResolved = await onResolveActivationBytes(checksum.trim().toLowerCase());
    if (!wasResolved) {
      setErrorMessage("Activation bytes could not be resolved for this checksum.");
    }
  };

  const handleHashCalculation = async (event: FormEvent) => {
    event.preventDefault();
    setErrorMessage("");

    if (!isValidActivationBytes(activationBytes)) {
      setErrorMessage("Activation bytes must contain exactly 8 hexadecimal characters.");
      return;
    }

    setIsCalculatingHash(true);
    try {
      const normalizedActivationBytes = activationBytes.trim().toLowerCase();
      setActivationBytes(normalizedActivationBytes);
      setChecksum(await calculateAudibleChecksum(normalizedActivationBytes));
    } catch (error) {
      console.error("Failed to calculate Audible hash:", error);
      setErrorMessage("The hash could not be calculated in this browser.");
    } finally {
      setIsCalculatingHash(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className={isDark ? "text-gray-300" : "text-gray-700"}>
        Resolve an Audible checksum and activation bytes in either direction.
      </p>

      <form className="space-y-2" onSubmit={handleActivationBytesLookup} noValidate>
        <label htmlFor={CHECKSUM_INPUT_ID} className={isDark ? "text-gray-300" : "text-gray-700"}>
          Checksum / Hash
        </label>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            id={CHECKSUM_INPUT_ID}
            type="text"
            value={checksum}
            onChange={(event) => {
              setChecksum(event.target.value);
              setErrorMessage("");
            }}
            aria-describedby={RESOLVER_ERROR_ID}
            autoCapitalize="none"
            autoComplete="off"
            spellCheck={false}
            className={inputClasses}
          />
          <button
            type="submit"
            className={buttonClasses}
            disabled={isResolvingActivationBytes}
          >
            {isResolvingActivationBytes ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Resolving...
              </span>
            ) : (
              "Get Activation Bytes"
            )}
          </button>
        </div>
      </form>

      <form className="space-y-2" onSubmit={handleHashCalculation} noValidate>
        <label
          htmlFor={ACTIVATION_BYTES_INPUT_ID}
          className={isDark ? "text-gray-300" : "text-gray-700"}
        >
          Activation Bytes
        </label>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            id={ACTIVATION_BYTES_INPUT_ID}
            type="text"
            value={activationBytes}
            onChange={(event) => {
              setActivationBytes(event.target.value);
              setErrorMessage("");
            }}
            aria-describedby={RESOLVER_ERROR_ID}
            autoCapitalize="none"
            autoComplete="off"
            spellCheck={false}
            className={inputClasses}
          />
          <button type="submit" className={buttonClasses} disabled={isCalculatingHash}>
            {isCalculatingHash ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Calculating...
              </span>
            ) : (
              "Get Hash"
            )}
          </button>
        </div>
      </form>

      <p
        id={RESOLVER_ERROR_ID}
        role="alert"
        aria-live="polite"
        className={`min-h-5 ${errorMessage ? (isDark ? "text-red-400" : "text-red-700") : ""}`}
      >
        {errorMessage}
      </p>
    </div>
  );
}
